import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import './ApproveRejectModal.css';

interface CancelOrderModalProps {
  show: boolean;
  handleClose: () => void;
  handleConfirm: (reason: string) => void;
  isLoading?: boolean;
}

const CancelOrderModal: React.FC<CancelOrderModalProps> = ({
  show,
  handleClose,
  handleConfirm,
  isLoading = false
}) => {
  const [reason, setReason] = useState('');
  const [validationError, setValidationError] = useState('');

  // Reset reason and validation error when modal opens/closes
  useEffect(() => {
    if (!show) {
      setReason('');
      setValidationError('');
    }
  }, [show]);

  const handleSubmit = () => {
    // Reason is required for cancellation
    if (!reason.trim()) {
      setValidationError('Reason is required for cancellation');
      return;
    }
    
    setValidationError('');
    handleConfirm(reason);
    setReason(''); // Reset reason after submission
  };

  const handleModalClose = () => {
    if (!isLoading) {
      setReason('');
      setValidationError('');
      handleClose();
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReason(e.target.value);
    // Clear validation error when user starts typing
    if (validationError && e.target.value.trim()) {
      setValidationError('');
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={handleModalClose}
      centered
      backdrop={isLoading ? 'static' : true}
      keyboard={!isLoading}
    >
      <Modal.Header closeButton={!isLoading}>
        <Modal.Title>Cancel Order?</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <p className="approve-reject-modal-message">
          Are you sure you want to cancel this order?
        </p>
        
        <Form.Group className="mb-3">
          <Form.Label>
            Reason <span className="text-danger">(Required)</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Reason for cancellation..."
            value={reason}
            onChange={handleReasonChange}
            disabled={isLoading}
            isInvalid={!!validationError}
          />
          {validationError && (
            <Form.Control.Feedback type="invalid">
              {validationError}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          variant="outline-secondary" 
          onClick={handleModalClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button 
          variant="danger"
          onClick={handleSubmit}
          disabled={isLoading || !reason.trim()}
        >
          {isLoading ? 'Processing...' : 'Cancel Order'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CancelOrderModal;
